import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';

import { Button } from 'ui-components/components/button';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './plan-summary.module.less';
import { useGetShipmentVolumeAddOnPayload } from './use-get-shipment-volume-add-on-payload';
import { useIsAcceptingPromotion } from './use-is-accepting-promotion';
import { useShouldRestoreCustomShipmentVolume } from './use-should-restore-custom-shipment-volume';
import { useGetAddOnsToRemove } from '../../migrate-plan-modal/hooks/use-get-addons-to-remove';
import { SUBSCRIPTION_UPGRADE_LINK } from '../../../../../../constants/analytics-events';
import { ADD_ANALYTICS_LICENSE } from '../../../../../../constants/add-ons';
import { ADD_USER } from '../../../../../../constants/features';
import { useFsuiContext } from '../../../../../../financial-services-ui/fsui-context-provider';
import { useIsNonWalletAccessVisible } from '../../../../../../hooks/non-wallet-access-hooks';
import { useModal } from '../../../../../../hooks/use-modal';
import { usePaymentMethodManager } from '../../../../../../hooks/use-payment-method-manager';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { LogService } from '../../../../../../services/log-service';
import Global from '../../../../../../store/global-selectors';
import { useManageSubscriptionDrawerContext } from '../../../context/manage-subscription-drawer-context';
import { usePaymentAndSubscriptionMegaContext } from '../../../context';
import { useGetNumberOfExtraUserSeats } from '../../../hooks/use-get-number-of-extra-user-seats';
import { useSelectedBasePlan, useGetMinNumberOfUserSeats } from '../../../hooks/use-plan-details';
import { UserDeactivationModal } from '../../../user-deactivation-modal';
import { BillingPlanDowngradeConfirmationModal } from '../billing-plan-downgrade-confirmation-modal/billing-plan-downgrade-confirmation-modal';
import { getIsDowngradingBillingPlan } from '../billing-plan-downgrade-confirmation-modal/get-is-downgrading-billing-plan';
import { ActivationTiming } from '../constants';
import { PlanGridConfig, useManageSubscriptionDrawerSectionVisibility } from '../use-manage-subscription-drawer-section-visibility';

export const UpdatePlanButton = () => {
  const {
    addOnsToAdd,
    addOnsToRemove,
    byoaEnabled,
    desiredNumberOfAdditionalUsers,
    desiredNumberOfAdditionalAnalyticsLicenses,
    changePlanHandler,
    desiredShipmentLimit,
    setIsUserAddOnModalOpen,
    isUserAddOnModalOpen,
    isOnAnnualBilling,
    isMigratingToNewestPlans,
    showAnnualPlans,
  } = useManageSubscriptionDrawerContext();

  const { billingInfo } = usePaymentAndSubscriptionMegaContext();
  const { paymentMethodId } = useFsuiContext();
  const { preferredPaymentMethod } = usePaymentMethodManager({
    transactionCategory: TransactionCategory.Subscription,
  });
  const { planGridConfig } = useManageSubscriptionDrawerSectionVisibility();

  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);

  const { currentBillingPlan, planToNonWalletAccessPlanMap = {} } = subscriptionData ?? {};

  const selectedPlan = useSelectedBasePlan();
  const currentNumberOfAdditionalUsers = useGetNumberOfExtraUserSeats();
  const currentAdditionalAnalyticsLicenses = currentBillingPlan?.additionalAnalyticsLicenses ?? 0;
  const isNewLineupPlanGridEnabled = planGridConfig === PlanGridConfig.GbbLineup;
  const numberOfUsersWithoutPlanSeats = useGetMinNumberOfUserSeats(selectedPlan?.billingPlanId);

  const currentBillingPlanId = currentBillingPlan?.billingPlanId;
  const selectedBillingPlanId = selectedPlan?.billingPlanId;
  const isSelectedPlanAnnual = selectedPlan?.isOnAnnualBilling ?? false;
  const isMigratingToNewestPlansToAnnual = isSelectedPlanAnnual && !isOnAnnualBilling;
  const isAttemptingToSwitchToMonthly = isOnAnnualBilling && !showAnnualPlans;

  const byoaPlan = planToNonWalletAccessPlanMap?.[selectedBillingPlanId];
  const shouldShowByoaToggle = useIsNonWalletAccessVisible();

  const [isUpdating, setIsUpdating] = useAsyncSafeState(false);

  const isPurchasingEnoughSeats = desiredNumberOfAdditionalUsers >= numberOfUsersWithoutPlanSeats;
  const doesNotHaveEnoughAdditionalSeats = numberOfUsersWithoutPlanSeats > 0 && !isPurchasingEnoughSeats;
  const isUserDeactivationRequired = isNewLineupPlanGridEnabled && doesNotHaveEnoughAdditionalSeats;

  const openUserDeactivationModal = useCallback(() => {
    setIsUserAddOnModalOpen(true);
  }, [setIsUserAddOnModalOpen]);

  const closeUserDeactivationModal = useCallback(() => {
    setIsUserAddOnModalOpen(false);
  }, [setIsUserAddOnModalOpen]);

  const {
    open: openBillingPlanDowngradeModal,
    close: closeBillingPlanDowngradeModal,
    isOpen: isBillingPlanDowngradeModalOpen,
  } = useModal();

  const getShipmentVolumeAddOnPayload = useGetShipmentVolumeAddOnPayload({
    desiredShipmentLimit,
    selectedPlan,
    isMigratingToNewestPlans,
  });

  const shouldRestoreCustomShipmentVolume = useShouldRestoreCustomShipmentVolume(desiredShipmentLimit);
  const migrationAddOnsToRemove = useGetAddOnsToRemove();

  const newPlanCode = byoaEnabled && byoaPlan ? byoaPlan.code : selectedPlan.code;
  const isAcceptingPromotion = useIsAcceptingPromotion(newPlanCode);

  // TODO: better notifications
  const updatePlan = useCallback(async () => {
    setIsUpdating(true);
    const isUpgrading = currentBillingPlan?.pricePerPeriod.value < selectedPlan.pricePerPeriod.value;

    // TODO: SPD-17606 - on a resubscribe, we need to add the add-ons with ActivationTiming.IMMEDIATELY
    const addOnPayloadToAdd = addOnsToAdd.map((code) => ({
      code,
      quantity: 1,
      activationTiming: ActivationTiming.END_OF_BILLING_CYCLE,
    }));
    if (currentNumberOfAdditionalUsers !== desiredNumberOfAdditionalUsers && desiredNumberOfAdditionalUsers > 0) {
      addOnPayloadToAdd.push({
        code: ADD_USER,
        quantity: desiredNumberOfAdditionalUsers,
        activationTiming: ActivationTiming.IMMEDIATELY,
      });
    }

    // When migrating, use migration-specific add-on removal (removes all except users, shipment volume, mesh/3pl)
    const addOnsPayloadToRemove = isMigratingToNewestPlans
      ? [...migrationAddOnsToRemove]
      : addOnsToRemove.map((code) => ({
          code,
          quantity: 0,
          activationTiming: isUpgrading ? ActivationTiming.IMMEDIATELY : ActivationTiming.END_OF_BILLING_CYCLE,
        }));
    if (currentNumberOfAdditionalUsers !== desiredNumberOfAdditionalUsers && desiredNumberOfAdditionalUsers === 0) {
      addOnsPayloadToRemove.push({
        code: ADD_USER,
        quantity: 0,
        activationTiming: ActivationTiming.IMMEDIATELY,
      });
    }

    const shipmentVolumeAddOnPayload = getShipmentVolumeAddOnPayload();
    if (shipmentVolumeAddOnPayload.addOnPayloadToAdd) {
      addOnPayloadToAdd.push(shipmentVolumeAddOnPayload.addOnPayloadToAdd);
    }
    if (shipmentVolumeAddOnPayload.addOnPayloadToRemove) {
      addOnsPayloadToRemove.push(shipmentVolumeAddOnPayload.addOnPayloadToRemove);
    }

    const isDecreasingAnalyticsLicenses = desiredNumberOfAdditionalAnalyticsLicenses < currentAdditionalAnalyticsLicenses;
    const isDowngrading = !isUpgrading && newPlanCode !== currentBillingPlan?.code;
    const isSamePlan = newPlanCode === currentBillingPlan?.code;
    const isChangingLicenseCount = desiredNumberOfAdditionalAnalyticsLicenses !== currentAdditionalAnalyticsLicenses;
    const analyticsLicenseTiming =
      isDowngrading || (isDecreasingAnalyticsLicenses && isSamePlan) ? ActivationTiming.END_OF_BILLING_CYCLE : ActivationTiming.IMMEDIATELY;
    if (isChangingLicenseCount && desiredNumberOfAdditionalAnalyticsLicenses > 0) {
      addOnPayloadToAdd.push({
        code: ADD_ANALYTICS_LICENSE,
        quantity: desiredNumberOfAdditionalAnalyticsLicenses,
        activationTiming: analyticsLicenseTiming,
      });
    }
    if (isChangingLicenseCount && desiredNumberOfAdditionalAnalyticsLicenses === 0 && currentAdditionalAnalyticsLicenses > 0) {
      addOnsPayloadToRemove.push({
        code: ADD_ANALYTICS_LICENSE,
        quantity: 0,
        activationTiming: analyticsLicenseTiming,
      });
    }

    const shouldForceChangeNow = isMigratingToNewestPlansToAnnual || isMigratingToNewestPlans;

    const shouldSkipPaymentMethodId = billingInfo?.isTokenized && paymentMethodId === preferredPaymentMethod?.id;

    const payload = {
      changePlanRequest: {
        newPlanCode,
        isDowngradeCancellation: false,
        skipUsersError: true,
        shouldRestoreCustomShipmentVolume,
        ...(shouldForceChangeNow && { shouldForceChangeNow }), // only add property when true to prevent obvious discovery in network requests
        shouldMigratePlanProfile: isMigratingToNewestPlans,
        isAcceptingPromotion,
      },
      addOnsToPurchase: addOnPayloadToAdd,
      addOnsToRemove: addOnsPayloadToRemove,
      paymentMethodId: shouldSkipPaymentMethodId ? null : paymentMethodId,
    };

    try {
      await changePlanHandler(payload, shouldShowByoaToggle && !byoaEnabled);
    } catch (e) {
      LogService.logError(new Error('Failed to change plan/users', { cause: e }), { payload });
    } finally {
      setIsUpdating(false);
    }

    Segment.trackEvent(SUBSCRIPTION_UPGRADE_LINK, {
      payload,
    });
  }, [
    setIsUpdating,
    addOnsToAdd,
    currentAdditionalAnalyticsLicenses,
    currentNumberOfAdditionalUsers,
    desiredNumberOfAdditionalAnalyticsLicenses,
    desiredNumberOfAdditionalUsers,
    currentBillingPlan?.pricePerPeriod.value,
    selectedPlan.pricePerPeriod.value,
    newPlanCode,
    addOnsToRemove,
    byoaEnabled,
    changePlanHandler,
    shouldRestoreCustomShipmentVolume,
    shouldShowByoaToggle,
    getShipmentVolumeAddOnPayload,
    paymentMethodId,
    preferredPaymentMethod?.id,
    isMigratingToNewestPlans,
    migrationAddOnsToRemove,
    isMigratingToNewestPlansToAnnual,
    billingInfo?.isTokenized,
    isAcceptingPromotion,
    currentBillingPlan?.code,
  ]);

  const onClick = useCallback(async () => {
    const isDowngradingBillingPlan = getIsDowngradingBillingPlan(currentBillingPlanId, selectedBillingPlanId);
    if (isDowngradingBillingPlan) {
      openBillingPlanDowngradeModal();
      return;
    }

    if (isUserDeactivationRequired) {
      openUserDeactivationModal();
      return;
    }

    await updatePlan();
  }, [
    currentBillingPlanId,
    isUserDeactivationRequired,
    openBillingPlanDowngradeModal,
    openUserDeactivationModal,
    selectedBillingPlanId,
    updatePlan,
  ]);

  const handlePlanDowngradeConfirm = useCallback(async () => {
    closeBillingPlanDowngradeModal();

    if (isUserDeactivationRequired) {
      openUserDeactivationModal();
      return;
    }

    await updatePlan();
  }, [closeBillingPlanDowngradeModal, isUserDeactivationRequired, openUserDeactivationModal, updatePlan]);

  const handleUserDowngradeConfirm = useCallback(async () => {
    closeUserDeactivationModal();
    await updatePlan();
  }, [closeUserDeactivationModal, updatePlan]);

  const handleUserDowngradeSkip = useCallback(async () => {
    closeUserDeactivationModal();
    await updatePlan();
  }, [closeUserDeactivationModal, updatePlan]);

  const handleDowngradeCancel = useCallback(() => {
    closeBillingPlanDowngradeModal();
  }, [closeBillingPlanDowngradeModal]);

  const isButtonDisabled = !paymentMethodId || isUpdating;

  return (
    <>
      {isAttemptingToSwitchToMonthly ? (
        <Button className={style['select-plan-btn']} disabled theme={THEME_CONSTANTS.secondary}>
          <FormattedMessage {...messages.contactCustomerSupportButtonText} />
        </Button>
      ) : (
        <Button className={style['select-plan-btn']} disabled={isButtonDisabled} onClick={onClick} theme={THEME_CONSTANTS.primary}>
          <FormattedMessage {...messages.updatePlanButtonText} />
        </Button>
      )}
      {isBillingPlanDowngradeModalOpen ? (
        <BillingPlanDowngradeConfirmationModal
          currentBillingPlanId={currentBillingPlanId}
          onClose={handleDowngradeCancel}
          onConfirm={handlePlanDowngradeConfirm}
          selectedBillingPlanId={selectedBillingPlanId}
        />
      ) : null}
      {isUserAddOnModalOpen && isNewLineupPlanGridEnabled ? (
        <UserDeactivationModal onClose={closeUserDeactivationModal} onSave={handleUserDowngradeConfirm} onSkip={handleUserDowngradeSkip} />
      ) : null}
    </>
  );
};
